<div class="flex flex-col h-full max-h-[80vh]">
  <!-- Header -->
  <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
    <div class="flex items-center gap-3">
      <h2 class="text-base font-semibold text-gray-900">
        {{ isViewMode ? 'Indikator anzeigen' : 'Indikator bearbeiten' }}
      </h2>
      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        {{ getIndikatorDisplayNumber() }}
      </span>
      <span *ngIf="isActuallyChecked() && computedStatusId" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white" [style.background-color]="computedStatusColor">
        {{ computedStatusId === '1' ? 'Kritisch' : (computedStatusId === '2' ? 'Warnung' : (computedStatusId === '3' ? 'OK' : '—')) }}
      </span>
    </div>
    <button 
      (click)="onClose()"
      mat-icon-button
      class="text-gray-400 hover:text-gray-600">
      <mat-icon class="w-4 h-4">close</mat-icon>
    </button>
  </div>

  <!-- Scrollable Content -->
  <div class="flex-1 overflow-y-auto px-4 py-4">

  <!-- Preview Information (Edit Mode Only) -->
  <!-- State 1: Not yet checked (checked_by is 0 or missing) -->
  <div *ngIf="isEditMode && !isActuallyChecked() && !previewInfo.isFirstSave" class="mb-6 p-4 bg-yellow-50 rounded-lg">
    <div class="flex items-center">
      <mat-icon class="text-yellow-600 mr-2">info</mat-icon>
      <span class="text-sm text-yellow-800">
        Indikator wurde noch nicht geprüft. Beim Speichern werden Datum, Uhrzeit und Prüfer automatisch gesetzt.
      </span>
    </div>
  </div>

  <!-- State 2: First save (will be checked now) -->
  <div *ngIf="isEditMode && previewInfo.isFirstSave" class="mb-6 p-4 bg-blue-50 rounded-lg">
    <h3 class="text-sm font-medium text-blue-900 mb-3">Wird gespeichert als:</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Wird geprüft von</label>
        <div class="text-sm text-blue-900">{{ previewInfo.willBeCheckedBy }}</div>
      </div>
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Wird geprüft am</label>
        <div class="text-sm text-blue-900">{{ previewInfo.willBeCheckedAt }}</div>
      </div>
    </div>
    <div class="mt-3 p-3 bg-blue-100 rounded-md">
      <div class="flex items-center">
        <mat-icon class="text-blue-600 mr-2">info</mat-icon>
        <span class="text-sm text-blue-800">
          Beim ersten Speichern werden Datum, Uhrzeit und Prüfer automatisch gesetzt.
        </span>
      </div>
    </div>
  </div>

  <!-- State 3: Already checked (has valid checked_by and checked date) -->
  <div *ngIf="isEditMode && isActuallyChecked() && !previewInfo.isFirstSave" class="mb-6 p-4 bg-blue-50 rounded-lg">
    <h3 class="text-sm font-medium text-blue-900 mb-3">Bereits gespeichert:</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Geprüft von</label>
        <div class="text-sm text-blue-900">{{ previewInfo.willBeCheckedBy }}</div>
      </div>
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Geprüft am</label>
        <div class="text-sm text-blue-900">{{ previewInfo.willBeCheckedAt }}</div>
      </div>
    </div>
  </div>

  <!-- Indikator Info -->
  <div class="mb-6 p-4 bg-gray-50 rounded-lg">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Location -->
      <div class="min-w-0">
        <label class="block text-sm font-medium text-gray-700 mb-1">Standort / Objekt</label>
        <div class="text-sm text-gray-900">
          <div class="font-medium break-words">{{ data.indikator.gebaeude_name }}</div>
          <div class="text-gray-600">{{ data.indikator.sektion_name }}</div>
          <div class="text-gray-500 text-xs mt-0.5">Raum: {{ data.indikator.room_name }}</div>
        </div>
      </div>
      <!-- Indikator Type & Name -->
      <div class="min-w-0">
        <label class="block text-sm font-medium text-gray-700 mb-1">Indikator</label>
        <div class="text-sm text-gray-900">
          <div class="font-medium break-words">{{ data.indikator.indikator_name }}</div>
          <div *ngIf="getIndikatorCode()" class="mt-0.5">
            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
              {{ getIndikatorCode() }}
            </span>
          </div>
        </div>
      </div>
      <!-- Schwellenwert -->
      <div class="min-w-0">
        <label class="block text-sm font-medium text-gray-700 mb-1">Schwellenwert</label>
        <div *ngIf="isViewMode" class="text-sm text-gray-900">
          {{ formatSchwellenwert(schwellenwertValue) }} {{ data.indikator.indikator?.einheit || '' }}
        </div>
        <div *ngIf="!isViewMode" class="flex items-center gap-2">
          <input
            type="number"
            step="1"
            [(ngModel)]="schwellenwertValue"
            [ngModelOptions]="{standalone: true}"
            placeholder="0"
            class="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            (change)="onSchwellenwertChange()">
          <span class="text-sm text-gray-500">{{ data.indikator.indikator?.einheit || '' }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Completion Info (View Mode Only) -->
  <div *ngIf="isViewMode && isActuallyChecked()" class="mb-6 p-4 bg-blue-50 rounded-lg">
    <h3 class="text-sm font-medium text-blue-900 mb-3">Bearbeitungsinformationen</h3>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Geprüft am</label>
        <div class="text-sm text-blue-900">{{ getFormattedCheckedDate() }}</div>
      </div>
      <div>
        <label class="block text-sm font-medium text-blue-700 mb-1">Geprüft von</label>
        <div class="text-sm text-blue-900">{{ getCheckedByUserName() }}</div>
      </div>
    </div>
  </div>

  <!-- Form -->
  <div class="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
  <h3 class="text-sm font-medium text-green-900 mb-3">Befunddaten</h3>
  <form [formGroup]="indikatorForm" class="space-y-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Wert -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Wert *</label>
        <input 
          type="number"
          step="1"
          formControlName="wert"
          placeholder="0"
          [readonly]="isViewMode"
          [class]="isViewMode ? 'w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600' : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'"
          (change)="roundWert()">
        <div *ngIf="indikatorForm.get('wert')?.invalid && indikatorForm.get('wert')?.touched" 
             class="text-red-600 text-xs mt-1">
          Wert ist erforderlich
        </div>
      </div>
    </div>

    <!-- Problem (Abweichungen) - hidden for now -->
    <div *ngIf="false">
      <app-multi-select
        [items]="abweichungenItems"
        [selectedItems]="selectedAbweichungen"
        [loading]="abweichungenLoading"
        [label]="'Problem (Abweichungen)'"
        [placeholder]="'Abweichungen auswählen...'"
        (selectionChange)="onAbweichungenChange($event)">
      </app-multi-select>
    </div>

    <!-- Maßnahmen -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Maßnahmen</label>
      <div *ngIf="!isViewMode">
        <button 
          type="button"
          (click)="openMassnahmenDialog()"
          [disabled]="massnahmenLoading"
          class="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
          <span class="text-gray-700">
            <span *ngIf="massnahmenLoading">Laden...</span>
            <span *ngIf="!massnahmenLoading && selectedMassnahmen.length === 0">Maßnahmen auswählen...</span>
            <span *ngIf="!massnahmenLoading && selectedMassnahmen.length > 0">{{ selectedMassnahmen.length }} Maßnahme(n) ausgewählt</span>
          </span>
          <mat-icon class="text-gray-400" style="font-size: 18px; width: 18px; height: 18px;">list</mat-icon>
        </button>
      </div>
      <!-- Display Selected Massnahmen (chips) -->
      <div *ngIf="selectedMassnahmen.length > 0" class="mt-2 flex flex-wrap gap-1.5">
        <span *ngFor="let item of selectedMassnahmen" 
              class="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-800 text-xs rounded-full border border-blue-200">
          {{ item.name }}
          <button *ngIf="!isViewMode" type="button" (click)="removeMassnahme(item)" class="ml-0.5 text-blue-400 hover:text-blue-600">
            <mat-icon style="font-size: 14px; width: 14px; height: 14px;">close</mat-icon>
          </button>
        </span>
      </div>
    </div>

    <!-- Produkt -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Produkt</label>
      <div *ngIf="!isViewMode">
        <button 
          type="button"
          (click)="openArtikelDialog()"
          [disabled]="artikelLoading"
          class="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
          <span class="text-gray-700">
            <span *ngIf="artikelLoading">Laden...</span>
            <span *ngIf="!artikelLoading && selectedArtikel.length === 0">Produkt auswählen...</span>
            <span *ngIf="!artikelLoading && selectedArtikel.length > 0">{{ selectedArtikel.length }} Produkt(e) ausgewählt</span>
          </span>
          <mat-icon class="text-gray-400" style="font-size: 18px; width: 18px; height: 18px;">list</mat-icon>
        </button>
      </div>
      <!-- Display Selected Produkte (chips with amounts) -->
      <div *ngIf="selectedArtikel.length > 0" class="mt-2 flex flex-wrap gap-1.5">
        <span *ngFor="let artikel of selectedArtikel" 
              class="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-800 text-xs rounded-full border border-green-200">
          {{ artikel.name }}<span *ngIf="artikel.amount && artikel.amount > 0"> ({{ artikel.amount }})</span>
          <button *ngIf="!isViewMode" type="button" (click)="removeArtikel(artikel)" class="ml-0.5 text-green-400 hover:text-green-600">
            <mat-icon style="font-size: 14px; width: 14px; height: 14px;">close</mat-icon>
          </button>
        </span>
      </div>
    </div>

    <!-- Anmerkung -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Anmerkung</label>
      <textarea 
        formControlName="anmerkung"
        placeholder="Anmerkung eingeben..."
        rows="2"
        [readonly]="isViewMode"
        [class]="isViewMode ? 'w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 resize-none' : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none'"></textarea>
    </div>
  </form>
  </div>
  </div>

  <!-- Fixed Actions -->
  <div class="flex justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-white">
    
    <!-- View Mode Buttons -->
    <div *ngIf="isViewMode" class="flex gap-3">
      <button 
        (click)="onClose()"
        mat-button
        class="text-gray-600 hover:text-gray-800">
        Schließen
      </button>
      <button 
        (click)="switchToEditMode()"
        mat-raised-button
        color="primary"
        class="bg-blue-600 hover:bg-blue-700 text-white">
        Bearbeiten
      </button>
    </div>

    <!-- Edit Mode Buttons -->
    <div *ngIf="isEditMode" class="flex gap-3">
      <button 
        (click)="onClose()"
        mat-button
        class="text-gray-600 hover:text-gray-800">
        Abbrechen
      </button>
      <button 
        (click)="onSave()"
        [disabled]="loading || !indikatorForm.valid"
        mat-raised-button
        color="primary"
        class="bg-blue-600 hover:bg-blue-700 text-white">
        <mat-icon *ngIf="loading" class="animate-spin mr-2">refresh</mat-icon>
        <span *ngIf="loading">Speichern...</span>
        <span *ngIf="!loading">Speichern</span>
      </button>
    </div>

  </div>
</div>
